import React from 'react';
import schoolLogo from '../assets/apple-icon-180x180.png';

// ── Logo SVG ────────────────────────────────────────────────
export function SchoolLogo({ size = 40 }) {
  return (
    <img
      src={schoolLogo}
      alt="Elpro Schools"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'block',
        borderRadius: 10,
      }}
    />
  );
}

// ── Color bar ───────────────────────────────────────────────
export function ColorBar() {
  return (
    <div style={{
      height: 4, borderRadius: 4,
      background: 'linear-gradient(90deg,#2E86C8 0%,#D42B2B 33%,#2E9E4F 66%,#E8940A 100%)',
      marginBottom: '1.8rem'
    }} />
  );
}

// ── Spinner ─────────────────────────────────────────────────
export function Spinner({ size = 20, color = '#2E86C8' }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      border: `2.5px solid ${color}30`,
      borderTop: `2.5px solid ${color}`,
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      flexShrink: 0,
    }} />
  );
}

// ── Badge ───────────────────────────────────────────────────
const badgeMap = {
  Pending:   { bg: '#FEF3E2', color: '#B55A00' },
  Approved:  { bg: '#E8F7ED', color: '#1a7a35' },
  Rejected:  { bg: '#FDEAEA', color: '#b01a1a' },
  Cancelled: { bg: '#EEF2F8', color: '#5a6a7e' },
};
export function Badge({ status }) {
  const s = badgeMap[status] || badgeMap.Pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
      background: s.bg, color: s.color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', opacity: .7 }} />
      {status}
    </span>
  );
}

// ── Card ────────────────────────────────────────────────────
export function Card({ children, style = {}, className = '' }) {
  return (
    <div className={`card ${className}`} style={{
      background: 'var(--white)', border: '1.5px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '1.4rem',
      boxShadow: 'var(--shadow)', ...style
    }}>
      {children}
    </div>
  );
}

// ── Button ──────────────────────────────────────────────────
export function Btn({ children, variant = 'ghost', size = 'md', onClick, disabled, style = {}, type = 'button' }) {
  const variants = {
    primary: { background: 'var(--blue)', color: 'white', border: 'none' },
    green:   { background: 'var(--green)', color: 'white', border: 'none' },
    red:     { background: 'var(--red-light)', color: 'var(--red)', border: '1.5px solid #f5c6c2' },
    ghost:   { background: 'var(--bg2)', color: 'var(--text1)', border: '1.5px solid var(--border)' },
    outline: { background: 'transparent', color: 'var(--blue)', border: '1.5px solid var(--blue)' },
  };
  const sizes = {
    sm: { padding: '5px 12px', fontSize: 12 },
    md: { padding: '9px 18px', fontSize: 13 },
    lg: { padding: '12px 24px', fontSize: 15 },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      borderRadius: 'var(--radius-sm)', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all .18s', fontFamily: 'var(--font-body)', opacity: disabled ? .6 : 1,
      ...variants[variant], ...sizes[size], ...style
    }}>
      {children}
    </button>
  );
}

// ── Form Group ──────────────────────────────────────────────
export function FormGroup({ label, children, hint, full = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: full ? '1 / -1' : undefined }}>
      {label && <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</label>}
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{hint}</span>}
    </div>
  );
}

// ── Input ───────────────────────────────────────────────────
export function Input({ style = {}, ...props }) {
  return (
    <input style={{
      padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
      fontSize: 14, fontFamily: 'var(--font-body)', color: 'var(--text1)', background: 'var(--white)',
      outline: 'none', width: '100%', transition: 'border .15s', ...style
    }} {...props} />
  );
}

// ── Select ──────────────────────────────────────────────────
export function Select({ children, style = {}, ...props }) {
  return (
    <select style={{
      padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
      fontSize: 14, fontFamily: 'var(--font-body)', color: 'var(--text1)', background: 'var(--white)',
      outline: 'none', width: '100%', appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
      ...style
    }} {...props}>
      {children}
    </select>
  );
}

// ── Textarea ────────────────────────────────────────────────
export function Textarea({ style = {}, speechToText = false, onChange, value, disabled, ...props }) {
  const [isListening, setIsListening] = React.useState(false);
  const [speechSupported, setSpeechSupported] = React.useState(false);
  const recognitionRef = React.useRef(null);
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    const SpeechRecognition = typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
    setSpeechSupported(Boolean(SpeechRecognition));
  }, []);

  React.useEffect(() => () => {
    if (recognitionRef.current) recognitionRef.current.stop();
  }, []);

  const appendTranscript = React.useCallback((transcript) => {
    const existing = (value ?? textareaRef.current?.value ?? '').toString();
    const spacer = existing && !existing.endsWith(' ') ? ' ' : '';
    const nextValue = `${existing}${spacer}${transcript}`.trim();

    if (onChange) {
      onChange({ target: { value: nextValue, name: props.name } });
      return;
    }
    if (textareaRef.current) textareaRef.current.value = nextValue;
  }, [onChange, props.name, value]);

  function toggleSpeechToText() {
    if (!speechSupported || disabled) return;

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const SpeechRecognition = typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onresult = (event) => {
      const spoken = Array.from(event.results || [])
        .map((result) => result[0]?.transcript || '')
        .join(' ')
        .trim();
      if (spoken) appendTranscript(spoken);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognitionRef.current = recognition;

    try {
      recognition.start();
      setIsListening(true);
    } catch {
      setIsListening(false);
    }
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{
          padding: '10px 14px',
          border: '1.5px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 14,
          fontFamily: 'var(--font-body)',
          color: 'var(--text1)',
          background: 'var(--white)',
          outline: 'none',
          width: '100%',
          resize: 'vertical',
          minHeight: 120,
          paddingRight: speechToText ? 56 : undefined,
          ...style,
        }}
        {...props}
      />
      {speechToText && (
        <button
          type="button"
          onClick={toggleSpeechToText}
          disabled={!speechSupported || disabled}
          title={!speechSupported ? 'Speech input not supported in this browser' : isListening ? 'Stop voice input' : 'Start voice input'}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 42,
            height: 34,
            borderRadius: 999,
            border: '1.5px solid var(--border)',
            background: isListening ? 'var(--red-light)' : 'var(--bg2)',
            color: isListening ? 'var(--red)' : 'var(--text2)',
            cursor: !speechSupported || disabled ? 'not-allowed' : 'pointer',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: !speechSupported || disabled ? 0.5 : 1,
          }}
        >
          {isListening ? 'Stop' : 'Mic'}
        </button>
      )}
    </div>
  );
}

// ── Stat Card ───────────────────────────────────────────────
export function StatCard({ label, value, icon, color }) {
  const bg = { blue: 'var(--blue-light)', green: 'var(--green-light)', orange: 'var(--orange-light)', red: 'var(--red-light)' };
  return (
    <div style={{
      background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)',
      padding: '1.2rem 1.4rem', display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: 'var(--shadow)', transition: 'transform .18s',
    }}
      onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseOut={e => e.currentTarget.style.transform = ''}
    >
      <div style={{ width: 48, height: 48, borderRadius: 12, background: bg[color], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: 26, fontWeight: 700 }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginTop: 1 }}>{label}</div>
      </div>
    </div>
  );
}

// ── Modal ───────────────────────────────────────────────────
export function Modal({ title, children, onClose }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(26,37,53,.45)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', backdropFilter: 'blur(3px)'
    }}>
      <div className="page-anim" style={{
        background: 'white', borderRadius: 20, width: '100%', maxWidth: 580,
        maxHeight: '90vh', overflowY: 'auto', padding: '2rem',
        boxShadow: '0 12px 48px rgba(26,37,53,.25)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: 18, fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg2)', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Empty state ─────────────────────────────────────────────
export function Empty({ icon = '📭', text = 'No data found' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 1rem', color: 'var(--text3)', gap: 12 }}>
      <div style={{ fontSize: 48, opacity: .4 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{text}</div>
    </div>
  );
}

// ── Page header ─────────────────────────────────────────────
export function PageHead({ title, subtitle, actions }) {
  return (
    <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: 22, fontWeight: 700 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>{subtitle}</div>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

// ── Table wrapper ───────────────────────────────────────────
export function TableWrap({ children }) {
  return (
    <div style={{ overflowX: 'auto', borderRadius: 'var(--radius)', border: '1.5px solid var(--border)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        {children}
      </table>
    </div>
  );
}

export function Th({ children }) {
  return <th style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.06em', background: 'var(--bg2)', whiteSpace: 'nowrap' }}>{children}</th>;
}

export function Td({ children, style = {} }) {
  return <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', verticalAlign: 'middle', ...style }}>{children}</td>;
}

export function RefCode({ value }) {
  return <span style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--bg2)', padding: '3px 7px', borderRadius: 5, color: 'var(--blue-dark)', fontWeight: 700 }}>{value}</span>;
}

export function AreaPill({ value }) {
  return <span style={{ background: 'var(--blue-light)', color: 'var(--blue-dark)', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>{value}</span>;
}

// ── Toast notification ──────────────────────────────────────
export function Toast({ msg, type = 'success', onClose }) {
  const colors = { success: '#2E9E4F', error: '#D42B2B', info: '#2E86C8' };
  React.useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 999,
      background: 'white', border: `2px solid ${colors[type]}`,
      borderRadius: 12, padding: '12px 18px', boxShadow: '0 4px 20px rgba(0,0,0,.12)',
      display: 'flex', alignItems: 'center', gap: 10, maxWidth: 360,
      animation: 'fadeIn .2s ease', fontWeight: 600, fontSize: 14
    }}>
      <span style={{ color: colors[type], fontSize: 18 }}>
        {type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}
      </span>
      <span style={{ flex: 1 }}>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
            fontWeight: 700, color: 'var(--text3)' }}>✕</button>
    </div>
  );
}

